import React, { Component } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faCheckCircle,
  faHammer,
  faLock,
} from '@fortawesome/free-solid-svg-icons';
import { TrevorIcon, Button, Tooltip } from '@trevor/ui-components';
import { get, post, userAction } from '@/util/helpers/ajax-helpers';
import { IntercomAPI } from '@/integrations/Intercom';
import { withReactRouter } from '@/hocs';
import googleTagManager from '@/integrations/googleTagManager';

import { useGetDatasourcesQuery } from '@/features/datasources/apiSlice';
import { OWNER } from '@/constants';
import {
  BIGQUERY,
  MYSQL,
  POSTGRES,
  REDSHIFT,
  SNOWFLAKE,
  SQLSERVER,
} from '../../util/enums/EnumDbType';
import AjaxSpinner from '../AjaxSpinner';
import Checkbox from '../configure/Checkbox';
import { CopyBox } from '../CopyBox';
import style from '../../css/module/credentials.css';
import flex from '../../css/module/flex.css';
import UploadFile from './UploadFile';
import ModalTip from '../ModalTip';
import { BIG_QUERY_KEY_FILE } from '../../util/Explainers';
import { BackToDatasourcesButton } from './BackToDatasourcesButton';
import { FinishButton } from './FinishButton';

const FILE_UPLOAD = 'file_upload';
const SECRET = 'secret';

const SELECT_MENU = 'select_menu';
const US = 'US';
const EU = 'EU';

library.add(faLock, faCheckCircle, faHammer);

let requestNumber = 0;

// field inputs. name must equal state name

const NICKNAME = {
  name: 'nickname',
  placeholder: 'Give your datasource a name',
  required: true,
};
const HOST = {
  name: 'host',
  placeholder: 'e.g. ec2-123.amazon.aws.com',
  required: true,
  disallowedChars: ['@', ':'],
};
const PORT = {
  name: 'port',
  placeholder: 'e.g. 5432',
  required: true,
};
const USER = {
  name: 'user',
  placeholder: 'username',
  required: true,
};

const PASSWORD = {
  name: 'password',
  placeholder: 'password',
  required: false,
};
const DATABASE = {
  name: 'database',
  placeholder: 'e.g. db123',
  required: true,
};

// big query

const BIG_QUERY_NAME = {
  type: FILE_UPLOAD,
  name: 'credentialsFile',
  alias: 'JSON key file',
  placeholder: 'JSON key file',
  required: true,
};

const BIG_QUERY_LOCATION = {
  type: SELECT_MENU,
  name: 'bigQueryRegion',
  alias: 'Region',
  options: [
    US,
    EU,
    'us-central1',
    'us-east1',
    'us-east4',
    'us-west1',
    'us-west2',
    'us-west3',
    'us-west4',
    'northamerica-northeast1',
    'northamerica-northeast2',
    'southamerica-east1',
    'southamerica-west1',
    'europe-west9',
    'europe-west8',
    'europe-west6',
    'europe-west4',
    'europe-west3',
    'europe-west2',
    'europe-west1',
    'europe-southwest1',
    'europe-central2',
    'europe-north1',
    'europe-north2',
    'asia-east1',
    'asia-east2',
    'asia-northeast1',
    'asia-northeast2',
    'asia-northeast3',
    'asia-southeast1',
    'asia-southeast2',
    'asia-south1',
    'asia-south2',
    'australia-southeast1',
    'australia-southeast2',
  ],
  required: true, // it has a default anyway
};

// snowflake

const WAREHOUSE = {
  name: 'warehouse',
  placeholder: 'WAREHOUSE',
  required: true,
};

// ssh

const SSH_HOST = {
  name: 'sshHost',
  placeholder: 'e.g. 136.69.115.122',
  required: true,
  alias: 'SSH Host',
  disallowedChars: ['@', ':'],
};
const SSH_PORT = {
  name: 'sshPort',
  placeholder: 'e.g. 22',
  required: true,
  alias: 'SSH Port',
};
const SSH_USER = {
  name: 'sshUser',
  placeholder: 'Username',
  required: true,
  alias: 'SSH User',
};
const SSH_PASSWORD = {
  name: 'sshPassword',
  placeholder: 'optional password',
  required: false,
  alias: 'Password',
};

const SSH_KEY = {
  type: FILE_UPLOAD,
  name: 'sshKeyFile',
  placeholder: 'Key file',
};

const SSH_PASSPHRASE = {
  name: 'sshKeyPassphrase',
  placeholder: 'key file passphrase',
  required: false,
  alias: 'passphrase',
};

// types
const MAINFORM = [NICKNAME, HOST, PORT, USER, PASSWORD, DATABASE];
const SNOWFLAKEFORM = [NICKNAME, HOST, USER, PASSWORD, DATABASE, WAREHOUSE];
const BIGQUERYFORM = [NICKNAME, BIG_QUERY_LOCATION, BIG_QUERY_NAME];
const SSHFORM = [
  SSH_HOST,
  SSH_PORT,
  SSH_USER,
  SSH_PASSWORD,
  SSH_KEY,
  SSH_PASSPHRASE,
];
const DB_TYPES = [POSTGRES, MYSQL, REDSHIFT, SQLSERVER, SNOWFLAKE, BIGQUERY];

const CREDENTIALS = 'credentials';
const SSH = 'ssh';

// map types to forms
const CREDENTIALS_MAP = {};
CREDENTIALS_MAP[POSTGRES] = MAINFORM;
CREDENTIALS_MAP[MYSQL] = MAINFORM;
CREDENTIALS_MAP[REDSHIFT] = MAINFORM;
CREDENTIALS_MAP[SQLSERVER] = MAINFORM;
CREDENTIALS_MAP[SNOWFLAKE] = SNOWFLAKEFORM;
CREDENTIALS_MAP[BIGQUERY] = BIGQUERYFORM;
CREDENTIALS_MAP[SSH] = SSHFORM;

const HELP_LINKS = {
  [POSTGRES]: [
    {
      host: 'AWS',
      link: 'https://guide.trevor.io/hc/en-us/articles/6809950743965-Connecting-to-Postgres-on-AWS-Amazon-Web-Services-',
    },
    {
      host: 'localhost',
      link: 'https://guide.trevor.io/hc/en-us/articles/7625725560349-Connecting-Trevor-io-to-your-localhost-database',
    },
  ],
  [MYSQL]: [
    {
      host: 'AWS',
      link: 'https://guide.trevor.io/hc/en-us/articles/6809951131933-Connecting-to-MySQL-on-AWS-Amazon-Web-Services-',
    },
    {
      host: 'localhost',
      link: 'https://guide.trevor.io/hc/en-us/articles/7625725560349-Connecting-Trevor-io-to-your-localhost-database',
    },
  ],
  [REDSHIFT]: [],
  [SQLSERVER]: [
    {
      host: 'Azure',
      link: 'https://guide.trevor.io/hc/en-us/articles/6809895225629',
    },
    {
      host: 'localhost',
      link: 'https://guide.trevor.io/hc/en-us/articles/7625725560349-Connecting-Trevor-io-to-your-localhost-database',
    },
  ],
  [SNOWFLAKE]: [],
  [BIGQUERY]: [
    {
      host: 'GCP',
      link: 'https://guide.trevor.io/hc/en-us/articles/6809935994269-Connecting-to-BigQuery-on-GCP-Google-Cloud-Platform-',
    },
  ],
};

const defaultPort = (dbType) => {
  switch (dbType) {
    case POSTGRES:
      return 5432;
    case MYSQL:
      return 3306;
    case REDSHIFT:
      return 5439;
    case SNOWFLAKE:
      return 123;
    case SQLSERVER:
      return 1433;
    default:
      return 5432;
  }
};

class EditCredentials extends Component {
  state = {
    credentials: {
      nickname: '',
      host: '',
      port: '',
      user: '',
      password: '',
      database: '',
      warehouse: '',
      credentialsFile: null, // used for BigQuery
      credentialsFileName: '', // used for BigQuery
      bigQueryRegion: US, // used for BigQuery
    },
    ssh: {
      sshHost: '',
      sshPort: 22,
      sshUser: '',
      sshPassword: '',
      sshKeyFile: null,
      sshKeyFileName: '',
      sshKeyPassphrase: '',
    },
    isPanoply: false,
    sshChecked: false,
    pageLoading: true, // true during initial page or while retrieving existing credentials
    showSuccessForm: false,
    dbType: null, // one of EnumDbType
    loading: false, // true if we're waiting for test credentials response
    firstTimeSetup: false, // true user is setting up a new db (as opposed to editing an existing one)
    connected: false, // true if we successfully connected with current credentials
    errors: null, // list of error messages from (4) attempts to connect
    showErrors: null, // list of booleans that map to errors
    datasourceId: null, // id of existing datasource (if updating existing, or already saved)
    testConnectionId: null, // the connection attempt that we're waiting for
    loadingProfile: true,
    isSelfHosted: false,
    isPortPristine: true,
  };

  static getDerivedStateFromProps(props, state) {
    const { databaseType } = props.urlParams;
    const newState = databaseType
      ? {
          firstTimeSetup: true,
          dbType: databaseType,
          credentials: {
            ...state.credentials,
            port: state.isPortPristine
              ? defaultPort(databaseType)
              : state.credentials.port, // set default port for db type
          },
          pageLoading: false,
        }
      : {};
    return { ...newState, hasOwnDatasource: props.hasOwnDatasource };
  }

  componentDidMount() {
    const { databaseType } = this.props.urlParams;
    get('loading profile', '/profile').then((json) => {
      this.setState({
        loadingProfile: false,
        isSelfHosted: json.selfHosted,
      });
    });
    if (window.location.hash && window.location.hash === '#panoply') {
      this.setState({
        isPanoply: true,
        firstTimeSetup: true,
        dbType: REDSHIFT,
        credentials: {
          // eslint-disable-next-line react/no-access-state-in-setstate
          ...this.state.credentials,
          port: defaultPort(REDSHIFT), // set default port for db type
          host: 'db.panoply.io',
          nickname: 'Panoply Data Warehouse',
        },
        pageLoading: false,
      });
    } else if (!databaseType) {
      // user is editing existing db
      get('retrieving credentials', '/setup/credentials')
        .then((json) => {
          const credentials = {
            nickname: json.nickname,
            host: json.creds.host,
            port: json.creds.port,
            user: json.creds.user,
            password: json.creds.password ? SECRET : '',
            passwordBackup: json.creds.password,
            database: json.creds.database,
            warehouse: json.creds.warehouse,
            credentialsFile: json.creds.credentialsFile,
            bigQueryRegion: json.creds.warehouse || US, // default to US
          };
          const ssh = {
            sshHost: json.creds.sshHost,
            sshPort: json.creds.sshPort,
            sshUser: json.creds.sshUser,
            sshPassword: json.creds.sshPassword ? SECRET : '',
            sshPasswordBackup: json.creds.sshPassword,
            sshKeyFile: json.creds.sshKeyFile,
            sshKeyPassphrase: json.creds.sshKeyPassphrase ? SECRET : '',
            sshKeyPassphraseBackup: json.creds.sshKeyPassphrase,
          };
          this.setState({
            credentials: { ...this.state.credentials, ...credentials },
            ssh: { ...this.state.ssh, ...ssh },
            sshChecked: !!json.creds.sshHost,
            dbType: json.creds.dbType,
            pageLoading: false,
          });
        })
        .catch(this.catchException);
    }
  }

  componentWillUnmount() {
    clearTimeout(this._pollForResponseTimeout);
  }

  catchException = (e) => {
    window.console.error(e);
    this.setState({
      loading: false,
    });
  };

  cancelPoll = () => {
    requestNumber++;
    clearTimeout(this._pollForResponseTimeout);
    this.setState({
      loading: false,
    });
  };

  pollForResponse = () => {
    const requestSent = ++requestNumber;
    get(
      'retrieving test credentials results',
      `/setup/testCredentials/${this.state.testConnectionId}`,
    )
      .then((json) => {
        if (requestSent !== requestNumber) {
          return;
        }
        this.setState({
          loading: !json.completed,
          connected: json.connected,
          errors: json.errors,
          showErrors:
            json.errors && json.errors.length > 0
              ? json.errors
                  .filter((item, i) => {
                    return json.errors.indexOf(item) === i;
                  })
                  .map(() => true)
              : null,
        });
        if (!json.completed) {
          // hasn't completed yet, so poll again
          this._pollForResponseTimeout = setTimeout(this.pollForResponse, 1000);
        } else if (json.connected) {
          // Only fire the GTM event if it is the 1st DB to be connected
          if (!this.state.hasOwnDatasource && !this.state.datasourceId) {
            googleTagManager.databaseConnected();
          }
          userAction('test-connection-succeeded');
          this.setState({
            pageLoading: true, // while saving credentials to server
          });
          post('saving credentials', '/save-datasource', {
            isStaticIp: json.staticRequired,
            existing: this.state.datasourceId,
            nickname: this.state.credentials.nickname,
            credentials: this.generateServerObject(),
          })
            .then((_json) => {
              this.setState({
                pageLoading: false,
                showSuccessForm: true,
                datasourceId: _json.datasourceId,
              });
            })
            .catch(this.catchException);
        } else if (json.errors.length > 0) {
          userAction('test-connection-failed');
          // force intercom to trigger a ping
          setTimeout(function () {
            const ts = Math.round(new Date().getTime() / 1000);
            Intercom('update', { dummy_attribute: ts });
          }, 2000);
        }
      })
      .catch(this.catchException);
  };

  generateServerObject = () => {
    const obj = {};
    // remove nickname as not accepted by server and then build object from state.
    CREDENTIALS_MAP[this.state.dbType]
      .filter((item) => item !== NICKNAME)
      .forEach((item) => (obj[item.name] = this.state.credentials[item.name]));
    obj.dbType = this.state.dbType;
    if (this.state.sshChecked) {
      SSHFORM.forEach((item) => (obj[item.name] = this.state.ssh[item.name]));
      obj.sshKeyFile = this.state.ssh.sshKeyFile;
      obj.sshKeyPassphrase = this.state.ssh.sshKeyPassphrase;
    }

    if (obj.password === SECRET) {
      obj.password = this.state.credentials.passwordBackup;
    }
    if (obj.sshPassword === SECRET) {
      obj.sshPassword = this.state.ssh.sshPasswordBackup;
    }
    if (obj.sshKeyPassphrase === SECRET) {
      obj.sshKeyPassphrase = this.state.ssh.sshKeyPassphraseBackup;
    }
    if (this.state.dbType === BIGQUERY) {
      // HACK: bigQuery region is stored in the warehouse field
      obj.warehouse = obj.bigQueryRegion;
      delete obj.bigQueryRegion;
    }

    return obj;
  };

  test = () => {
    const incompleteItemsArray = this.validateForm();
    if (incompleteItemsArray.length > 0) {
      return;
    }
    this.setState({
      loading: true,
      connected: false, // reset
      errors: null,
      showErrors: null, // reset
      testConnectionId: null, // reset
    });

    const requestSent = ++requestNumber;
    post(
      'sending credentials',
      '/setup/testCredentials',
      this.generateServerObject(),
    )
      .then((json) => {
        if (requestSent !== requestNumber) {
          return;
        }
        this.setState({
          testConnectionId: json.testConnectionId,
        });
        this.pollForResponse();
      })
      .catch(this.catchException);
  };

  handleChange = (name, value, isSSH) => {
    const toUpdate = isSSH ? SSH : CREDENTIALS;
    const copyOfCredentials = { ...this.state[toUpdate] };
    copyOfCredentials[name] = value;
    if (name === 'port' && this.state.isPortPristine) {
      this.setState({
        isPortPristine: false,
      });
    }
    this.setState({
      [toUpdate]: copyOfCredentials,
    });
  };

  generateForm = (type) => {
    const form = CREDENTIALS_MAP[type]; // get the form elements
    const values = this.state[type === SSH ? SSH : CREDENTIALS];
    const display = form.map((input, i) => {
      if (input.type === FILE_UPLOAD) {
        return this.fileUpload(values, type, input, i);
      }
      if (input.type === SELECT_MENU) {
        return this.selectMenu(values, type, input, i);
      }
      return this.formInput(values, type, input, i);
    });
    return display;
  };

  capitalize = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  inputIssue = (input) => {
    const list = input.disallowedChars.map((c, i) => {
      const or = i === input.disallowedChars.length - 1 ? 'or ' : '';
      const comma = i < input.disallowedChars.length - 2 ? ', ' : ' ';
      return <span key={i}>{`${or}"${c}"${comma}`}</span>;
    });
    return (
      <span>
        {input.name} should not contain {list}
      </span>
    );
  };

  // check port contains only numbers
  validatePort = (value) => {
    const containsNumber = /^\d+$/.test(value);
    return !containsNumber;
  };

  inputType = (input) => {
    switch (input) {
      case SSH_PASSPHRASE:
      case SSH_PASSWORD:
      case PASSWORD:
        return 'password';
      default:
        return 'text';
    }
  };

  placeholders = (input) => {
    const { dbType } = this.state;
    switch (input) {
      case HOST: {
        switch (dbType) {
          case SNOWFLAKE:
            return 'ab123.snowflakecomputing.com';
          default:
            return input.placeholder;
        }
      }
      case DATABASE: {
        switch (dbType) {
          case MYSQL:
            return 'The schema to connect to';
          case SNOWFLAKE:
            return 'name of the default database to connect to';
          default:
            return input.placeholder;
        }
      }
      case USER: {
        switch (dbType) {
          case SNOWFLAKE:
            return input.placeholder.toUpperCase();
          default:
            return input.placeholder;
        }
      }
      case PORT:
        return defaultPort(this.state.dbType);
      default:
        return input.placeholder;
    }
  };

  selectMenu = (values, type, input, i) => {
    const value = values[input.name];
    return (
      <div
        key={i}
        className="form-group"
        style={{ display: 'flex', marginBottom: '1rem', alignItems: 'center' }}
      >
        <label className={`text-right ${style.colLeft}`}>
          <span className={style.labelInner}>{input.alias || input.name}</span>
        </label>
        <div className={style.colRight}>
          <select
            style={{ maxWidth: '200px', height: '35px' }}
            value={value}
            className="form-control"
            onChange={(e) =>
              this.handleChange(input.name, e.target.value, type === SSH)
            }
          >
            {input.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  hasErrors = (formValue, input) => {
    return !formValue || formValue.length === 0
      ? false // no content yet so no issues.
      : input === PORT || input === SSH_PORT
      ? this.validatePort(formValue)
      : !input.disallowedChars
      ? false
      : input.disallowedChars.some((c) => formValue.includes(c));
  };

  formInput = (values, type, input, index) => {
    const labelName = this.capitalize(input.alias ? input.alias : input.name);

    const formValue = values[input.name] || '';
    const issueExists = this.hasErrors(formValue, input);
    const issueClass = !issueExists ? '' : style.inputIssue;

    const formInput = (
      <input
        type={this.inputType(input)}
        name={input.name}
        value={formValue}
        onChange={(e) =>
          this.handleChange(input.name, e.target.value, type === SSH)
        }
        className={`form-control ${issueClass}`}
        placeholder={this.placeholders(input)}
        disabled={this.state.loading}
        id={`field-${input.name}`}
        autoComplete="new-password"
      />
    );

    const inputToDisplay = !issueExists ? (
      formInput
    ) : (
      <span>
        <span data-tooltip-id={input.name}>{formInput}</span>
        <Tooltip id={input.name}>
          {input === PORT || input === SSH_PORT
            ? 'Must be an integer'
            : this.inputIssue(input)}
        </Tooltip>
      </span>
    );

    return (
      <div
        key={index}
        className="form-group"
        style={{ display: 'flex', marginBottom: '1rem', alignItems: 'center' }}
      >
        <label className={`text-right ${style.colLeft}`}>
          <span className={style.labelInner}>{labelName}</span>
        </label>
        <div className={style.colRight}>{inputToDisplay}</div>
      </div>
    );
  };

  fileUpload = (values, type, input, i) => {
    const { sshChecked } = this.state;

    const target = type === SSH ? SSH : CREDENTIALS;
    const formValue = values[input.name];

    return (
      <div
        key={i}
        style={{ display: 'flex', marginBottom: '1rem', alignItems: 'center' }}
      >
        <label className={`text-right ${style.colLeft}`}>
          <span className={style.labelInner}>{input.placeholder}</span>
        </label>
        <div className={style.colRight} style={{ flexDirection: 'row' }}>
          <div
            className={flex.alignItemsCenter}
            style={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              maxWidth: sshChecked ? '185px' : '300px',
            }}
          >
            <span>
              <UploadFile
                disabled={this.state.loading}
                content={formValue}
                onChange={(content, fileName) =>
                  this.uploadKeyFile(content, input, target, fileName || '')
                }
              />
            </span>
            {values[`${input.name}Name`].length > 0 ? (
              <small className="ellipsis">
                &nbsp;{values[`${input.name}Name`]}
              </small>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  uploadKeyFile = (content, input, target, fileName) => {
    const newObj = { ...this.state[target] };
    newObj[input.name] = content;
    newObj[`${input.name}Name`] = fileName;
    this.setState({
      [target]: newObj,
    });
  };

  inCompleteItems = (array, type) => {
    const formFields = CREDENTIALS_MAP[type === SSH ? SSH : this.state.dbType];

    return array
      .filter((item) => {
        const missingValue =
          item.required &&
          (!this.state[type][item.name] ||
            this.state[type][item.name].length === 0);
        const formValue = this.state[type][item.name] || '';
        const input = formFields.find((x) => x.name === item.name);
        const hasErrors = this.hasErrors(formValue, input);
        return !!(missingValue || hasErrors);
      })
      .map((item) => {
        return { type, name: item.alias ? item.alias : item.name };
      });
  };

  validateForm = () => {
    const { sshChecked, dbType } = this.state;
    const toValidate = CREDENTIALS_MAP[dbType]; // form items to validate
    const mainForm = this.inCompleteItems(toValidate, CREDENTIALS); // incomplete items from main creds
    return sshChecked
      ? [...mainForm, ...this.inCompleteItems(SSHFORM, SSH)]
      : mainForm;
  };

  cleanName = (type) => {
    switch (type) {
      case POSTGRES:
        return 'PostgreSQL';
      case MYSQL:
        return 'MySQL';
      case SQLSERVER:
        return 'SQL Server';
      case BIGQUERY:
        return 'BigQuery';
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  incomplete = (incompleteItems) => {
    return incompleteItems.map((item, i) => {
      const comma = i > 0 ? ', ' : '';
      const a = item.type === SSH ? ' an ' : ' a ';
      return <span key={i}>{comma + a + this.capitalize(item.name)}</span>;
    });
  };

  testConnectionButton = () => {
    const ready = this.validateForm().length === 0;
    const pulseClass = ready ? style.pulsate : '';

    const button = (
      <Button
        className={pulseClass}
        onClick={() => this.test()}
        disabled={!ready}
      >
        Test connection
      </Button>
    );

    if (ready) {
      return button;
    }
    return (
      <span>
        <span data-tooltip-id="button">{button}</span>
        <Tooltip id="button">
          Please add {this.incomplete(this.validateForm())}
        </Tooltip>
      </span>
    );
  };

  hideError = (errorToHide) => {
    const { errors, showErrors } = this.state;
    const index = errors.indexOf(errorToHide);
    const copyOfShowErrors = [...showErrors];
    copyOfShowErrors[index] = false;
    this.setState({
      showErrors: copyOfShowErrors,
    });
  };

  displayErrors = () => {
    const { errors, showErrors } = this.state;
    const uniqueErrors = errors.filter((item, i) => {
      return errors.indexOf(item) === i;
    });
    return uniqueErrors.map((error, i) => {
      if (!showErrors[i]) {
        return null;
      }
      return (
        <div
          key={i}
          className={`alert d-flex ${style.errorAlert} justify-content-between align-items-center`}
          role="alert"
        >
          <span className="flex-grow-1">
            {error
              .trim()
              .split('\n')
              .map((line, x) => (
                <span key={x}>
                  {line}
                  <br />
                </span>
              ))}
          </span>
          <span
            className={style.closeError}
            onClick={() => this.hideError(error)}
          >
            &times;
          </span>
        </div>
      );
    });
  };

  changeDbType = (e) => {
    const newDbType = e.target.value;
    this.setState({
      dbType: newDbType,
      sshChecked: false,
    });
    if (this.state.firstTimeSetup) {
      this.props.navigate(`/setup/${newDbType}`);
    }
  };

  helpLinks = (db) => {
    const articles = HELP_LINKS[db];
    if (articles.length > 0) {
      const list = articles.map((article, i) => {
        const text = `Connect to ${this.cleanName(db)} on ${article.host}`;
        const action = `open-guide-${text.replace(/\s/g, '-').toLowerCase()}`;
        return (
          <small
            onClick={() => this.openHelpLink(article.link, action)}
            key={i}
            className="link"
          >
            Connect to {this.cleanName(db)} on {article.host}
          </small>
        );
      });
      return <div className={style.quickLinks}>{list}</div>;
    }
    return null;
  };

  openHelpLink = (link, action) => {
    window.open(link, '_blank');
    userAction(action);
  };

  render() {
    const {
      pageLoading,
      showSuccessForm,
      firstTimeSetup,
      datasourceId,
      isSelfHosted,
      loadingProfile,
    } = this.state;

    if (pageLoading) {
      return (
        <div
          className={`${flex.flex}${flex.column}${flex.grow} w-100 ${flex.alignItemsCenter} ${flex.justifyContentCenter}`}
          style={{ height: '100vh' }}
        >
          <AjaxSpinner />
        </div>
      );
    }

    const type = this.state.dbType;

    return (
      <div className={style.page}>
        <BackToDatasourcesButton />
        {!showSuccessForm && (
          <div className={style.content}>
            <div className={`${flex.row} ${style.titleArea}`}>
              <div className={`${flex.column} ${flex.justifyContentBetween}`}>
                <div>
                  <h1 className={flex.alignItemsStart} style={{ marginTop: 0 }}>
                    Connect your{' '}
                    {this.state.isPanoply ? 'Panoply' : this.cleanName(type)}{' '}
                    database
                  </h1>
                  <h2 className="text-align-left">
                    <FontAwesomeIcon
                      icon="lock"
                      style={{
                        color: '#666',
                        fontSize: '0.95em',
                        marginRight: '5px',
                      }}
                    />
                    Trevor uses a secure, read-only transaction, and doesn't
                    store a copy of your database. Security is our&nbsp;
                    <a href="https://trevor.io/security/" target="_blank">
                      first priority
                    </a>
                    .
                  </h2>
                </div>
                <div style={{ display: 'flex' }}>
                  <div style={{ paddingRight: '25px' }}>
                    <div className={style.colRight}>
                      <select
                        value={this.state.dbType}
                        onChange={this.changeDbType}
                        disabled={this.state.loading}
                        style={{
                          height: '38px',
                          paddingInline: '10px',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                        }}
                      >
                        {DB_TYPES.map((_type) => (
                          <option key={_type} value={_type}>
                            {this.cleanName(_type)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {this.state.loading || type === BIGQUERY ? null : (
                    <div
                      className={`${style.sshContainer} ${flex.row} ${flex.alignItemsCenter}`}
                    >
                      <div className={flex.alignItemsCenter}>
                        <Checkbox
                          selected={this.state.sshChecked}
                          handleChange={() =>
                            this.setState({
                              sshChecked: !this.state.sshChecked,
                            })
                          }
                        />
                        <b
                          style={{ marginLeft: '10px' }}
                          onClick={() =>
                            this.setState({
                              sshChecked: !this.state.sshChecked,
                            })
                          }
                        >
                          Connect via SSH
                        </b>
                        &nbsp;
                        <small>
                          (e.g. via a{' '}
                          <a
                            href="https://medium.com/@trevormydata/securely-connecting-external-tools-to-your-rds-database-via-ssh-20ab980fb98"
                            target="_blank"
                          >
                            bastion host
                          </a>
                          )
                        </small>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className={style.linksContainer}>
                <div
                  className={`${style.container} ${style.linksBox}`}
                  style={{ margin: '0px' }}
                >
                  <span
                    className={`${flex.justifyContentBetween} ${flex.alignItemsCenter} link`}
                    onClick={() =>
                      this.openHelpLink(
                        'https://guide.trevor.io/hc/en-us/categories/6173906853917--Connecting-your-database',
                        'open-guide-need-help-connecting',
                      )
                    }
                  >
                    <b>Need help connecting?</b>
                    <TrevorIcon name="open-window" size={13} />
                  </span>
                  {HELP_LINKS[type].length > 0 ? (
                    <small className="text-muted">Quick links</small>
                  ) : null}
                  {this.helpLinks(type)}
                </div>
              </div>
            </div>
          </div>
        )}

        {!this.state.errors ? null : (
          <div className={style.content}>{this.displayErrors()}</div>
        )}

        {showSuccessForm ? (
          <div className={`${style.success} ${style.marginAuto}`}>
            <div
              className={`${style.container} ${style.notification}`}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <FontAwesomeIcon
                icon="check-circle"
                style={{
                  fontSize: '2em',
                  marginRight: '10px',
                  color: '5fba7d',
                }}
              />
              <span style={{ marginRight: 'auto' }}>
                Successfully connected to{' '}
                <b>{this.state.credentials.nickname}</b>.
              </span>
              <Button
                variant="secondary"
                onClick={() => this.setState({ showSuccessForm: false })}
              >
                Edit connection
              </Button>
            </div>
            <FinishButton
              datasourceId={datasourceId}
              firstTimeSetup={firstTimeSetup}
            />
          </div>
        ) : (
          <div>
            <div className="w-100">
              <div
                className={style.container}
                style={{ display: 'flex', gap: '30px' }}
              >
                <div className={flex.grow}>
                  <div className="w-100" style={{ display: 'flex' }}>
                    <div style={{ flexGrow: 1 }}>
                      <form>{this.generateForm(type)}</form>
                      {type === BIGQUERY &&
                      !this.state.credentials.bigQueryFile ? (
                        <ModalTip
                          title="How to find your Key File"
                          body={BIG_QUERY_KEY_FILE}
                        >
                          <small
                            className="text-muted"
                            style={{
                              paddingLeft: '100px',
                              marginTop: '10px',
                              textDecoration: 'underline',
                            }}
                          >
                            Need help finding your service account key file?
                          </small>
                        </ModalTip>
                      ) : null}
                    </div>
                    {!this.state.sshChecked ? null : (
                      <div style={{ flexGrow: 1 }}>
                        {this.generateForm(SSH)}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <div className={style.connectSection}>
                    {isSelfHosted || loadingProfile ? (
                      <div /> // empty div needed maintain flex justify-content-between positioning (otherwise test button will jump to the top)
                    ) : (
                      <div>
                        <small
                          className={`form-text text-muted ${style.leftAlign} ${style.firewallD}`}
                          style={{ display: 'block' }}
                        >
                          Do you need to whitelist our IPs?
                        </small>
                        <div className="d-flex flex-column">
                          {type === SNOWFLAKE ? (
                            <>
                              <CopyBox
                                value="54.173.229.200/32"
                                className={`${style.copyBox} ${style.copyBoxOne} mb3 w-100`}
                              />
                              <CopyBox
                                value="54.175.230.252/32"
                                className={`${style.copyBox} ${style.copyBoxTwo} mb3 w-100`}
                              />
                            </>
                          ) : (
                            <>
                              <CopyBox
                                value="34.192.31.89/32"
                                className={`${style.copyBox} ${style.copyBoxOne} mb3 w-100`}
                              />
                              <CopyBox
                                value="34.192.37.108/32"
                                className={`${style.copyBox} ${style.copyBoxTwo} mb3 w-100`}
                              />
                            </>
                          )}
                        </div>
                        {type === SNOWFLAKE ? (
                          <small
                            className={`form-text text-muted ${style.leftAlign}`}
                            style={{ marginTop: '12px', display: 'block' }}
                          >
                            (Static IPs: 54.173.229.200 & 54.175.230.252)
                          </small>
                        ) : (
                          <small
                            className={`form-text text-muted ${style.leftAlign}`}
                            style={{ marginTop: '12px', display: 'block' }}
                          >
                            (Static IPs: 34.192.31.89 & 34.192.37.108)
                          </small>
                        )}
                      </div>
                    )}
                    <div
                      className={`${style.testButtonContainer} margin-top-15`}
                    >
                      {this.state.loading || showSuccessForm ? (
                        <>
                          <AjaxSpinner small />
                          <small
                            className={style.cancelTest}
                            onClick={this.cancelPoll}
                          >
                            cancel
                          </small>
                        </>
                      ) : (
                        this.testConnectionButton()
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={style.content}>
              <h4>
                <b>Problem connecting?</b>
              </h4>{' '}
              <div style={{ fontSize: '1.1em' }}>
                <span className="link" onClick={IntercomAPI.show}>
                  Chat with us.
                </span>
                <span>&nbsp; We're happy to help.</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export const withHasOwnDatasource = (ComponentToRender) => {
  return function (props) {
    const { data } = useGetDatasourcesQuery();
    const hasOwnDatasource = data?.datasources.some(
      (datasource) => datasource.role === OWNER,
    );

    return <ComponentToRender hasOwnDatasource={hasOwnDatasource} {...props} />;
  };
};

export default withReactRouter(withHasOwnDatasource(EditCredentials));
